/*
 * Product-owned adaptation of @a2000/taskbar 0.0.34.
 * Upstream source: https://github.com/andricokaroulla/a2k (MIT License).
 * The source-shaped components are kept local so npm ci cannot replace the
 * Cangjie Compat navigation and account behavior.
 */
import { html, css, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./a2k-panel/lib/src/a2k-panel.js";
import "./a2k-stack/lib/src/a2k-stack.js";
import "./a2k-icon/a2k-icon";
import "./a2k-button/lib/src/a2k-button.js";
import { i18n } from "../i18n";
import { navigate, type ModuleName } from "../router";
import { session } from "../auth/session";

const menuModules: Array<[ModuleName, string]> = [
  ["overview", "windows-icon"],
  ["knowledge", "documents-icon"],
  ["rag", "network-icon"],
  ["jobs", "info-icon"],
  ["settings", "windows-icon"],
];

@customElement("cpl-start-menu-item")
export class ProductStartMenuItem extends LitElement {
  static styles = css`
    #start-menu-item {
      min-height: var(--start-menu-item-height);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      padding-inline: var(--start-menu-item-padding);
      gap: var(--start-menu-item-gap);
      cursor: pointer;
      width: 100%;
      border: 0;
      font: inherit;
      text-align: left;
    }
    #start-menu-item:hover,
    #start-menu-item[data-active="true"] {
      color: var(--color-white);
      background: var(--start-menu-background-hover);
    }
    #icon-wrapper {
      width: var(--start-menu-icon-size);
      height: var(--start-menu-icon-size);
      flex: 0 0 var(--start-menu-icon-size);
    }
    #text-wrapper {
      flex: 1;
      margin: 0;
      white-space: nowrap;
    }
  `;

  @property({ type: Boolean }) active = false;

  render() {
    return html`<div id="start-menu-item" data-active=${String(this.active)}>
      <div id="icon-wrapper"><slot name="icon"></slot></div>
      <p id="text-wrapper"><slot></slot></p>
    </div>`;
  }
}

@customElement("cpl-start-menu")
export class ProductStartMenu extends LitElement {
  static styles = css`
    #start-menu {
      position: absolute;
      left: 0;
      bottom: calc(100% + 2px);
      width: min(310px, calc(100vw - 16px));
      max-height: calc(100vh - var(--taskbar-height) - 8px);
      display: none;
      z-index: 1200;
    }
    #start-menu.open {
      display: block;
    }
    #content {
      display: flex;
      flex-direction: row;
      max-height: calc(100vh - var(--taskbar-height) - 8px);
      overflow: hidden;
    }
    #side-strip {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      background: var(--start-menu-strip-background-gradient);
      padding: var(--spacing-100) var(--spacing-50);
      min-height: 300px;
    }
    #side-strip p {
      color: white;
      font-weight: var(--font-weight-600);
      margin: 0;
    }
    #stack-wrapper {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .menu-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }
    .menu-action {
      display: block;
      width: 100%;
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      font: inherit;
      text-align: left;
    }
    .menu-account,
    .menu-footer {
      border-top: 1px solid #888;
      box-shadow: inset 0 1px #fff;
    }
    .menu-account {
      margin-top: 2px;
      padding-top: 2px;
    }
    .menu-footer {
      flex: 0 0 auto;
      padding-top: 2px;
    }
    .menu-status {
      margin: 0;
      padding: 5px 12px 3px;
      color: #444;
      font-size: 12px;
    }
    @media (max-width: 650px) {
      #start-menu {
        width: min(292px, calc(100vw - 16px));
      }
      #side-strip {
        min-height: 260px;
      }
    }
  `;

  @property({ type: Boolean }) open = false;
  @property({ type: Boolean }) authenticated = false;
  @property() active: ModuleName = "overview";

  private choose(module: ModuleName) {
    this.dispatchEvent(
      new CustomEvent("close-menu", { bubbles: true, composed: true }),
    );
    navigate(module);
  }

  private openDocs() {
    this.dispatchEvent(
      new CustomEvent("close-menu", { bubbles: true, composed: true }),
    );
    window.location.assign("/api-docs");
  }

  private signOut() {
    this.dispatchEvent(
      new CustomEvent("taskbar-logout", { bubbles: true, composed: true }),
    );
  }

  private signIn() {
    window.location.assign("/");
  }

  private accountAction(action: "profile" | "password") {
    this.dispatchEvent(
      new CustomEvent(`taskbar-${action}`, { bubbles: true, composed: true }),
    );
    this.dispatchEvent(
      new CustomEvent("close-menu", { bubbles: true, composed: true }),
    );
  }

  render() {
    const isOpen = String(this.open) === "true";
    return html`<div id="start-menu" class=${isOpen ? "open" : ""}>
      <a2k-panel>
        <div id="content">
          <div id="side-strip"><p>CANGJIE CAMPAT</p></div>
          <div id="stack-wrapper">
            <div class="menu-scroll">
              <a2k-stack>
                ${(this.authenticated ? menuModules : [])
                  .filter(
                    ([id]) =>
                      id !== "settings" ||
                      session.user?.role === "administrator",
                  )
                  .map(
                    ([module, icon]) =>
                      html`<button
                        type="button"
                        class="menu-action"
                        data-module=${module}
                        aria-current=${module === this.active ? "page" : "false"}
                        @click=${() => this.choose(module)}
                      >
                        <cpl-start-menu-item .active=${module === this.active}>
                          <span slot="icon"
                            ><a2k-icon icon=${icon}></a2k-icon
                          ></span>
                          ${i18n.t(module)}
                        </cpl-start-menu-item>
                      </button>`,
                  )}
                <button
                  type="button"
                  class="menu-action"
                  data-action="docs"
                  @click=${this.openDocs}
                >
                  <cpl-start-menu-item>
                    <span slot="icon"
                      ><a2k-icon icon="internet-icon"></a2k-icon
                    ></span>
                    ${i18n.t("docs")}
                  </cpl-start-menu-item>
                </button>
              </a2k-stack>
              ${
                this.authenticated
                  ? html`<div class="menu-account">
                      <button
                        type="button"
                        class="menu-action"
                        data-action="profile"
                        @click=${() => this.accountAction("profile")}
                      >
                        <cpl-start-menu-item
                          ><span slot="icon"
                            ><a2k-icon icon="user98-icon"></a2k-icon></span
                          >${i18n.t("profile")}</cpl-start-menu-item
                        >
                      </button>
                      <button
                        type="button"
                        class="menu-action"
                        data-action="password"
                        @click=${() => this.accountAction("password")}
                      >
                        <cpl-start-menu-item
                          ><span slot="icon"
                            ><a2k-icon icon="key98-icon"></a2k-icon></span
                          >${i18n.t("changePassword")}</cpl-start-menu-item
                        >
                      </button>
                    </div>`
                  : html`<button
                      type="button"
                      class="menu-action"
                      data-action="sign-in"
                      @click=${this.signIn}
                    >
                      <cpl-start-menu-item
                        ><span slot="icon"
                          ><a2k-icon icon="user98-icon"></a2k-icon></span
                        >${i18n.t("signIn")}</cpl-start-menu-item
                      >
                    </button>`
              }
            </div>
            <div class="menu-footer">
              <p class="menu-status">${i18n.t("operational")}</p>
              ${
                this.authenticated
                  ? html`<button
                      type="button"
                      class="menu-action"
                      data-action="sign-out"
                      @click=${this.signOut}
                    >
                      <cpl-start-menu-item
                        ><span slot="icon"
                          ><a2k-icon icon="shut-down-icon"></a2k-icon></span
                        >${i18n.t("signOut")}</cpl-start-menu-item
                      >
                    </button>`
                  : null
              }
            </div>
          </div>
        </div>
      </a2k-panel>
    </div>`;
  }
}

@customElement("cpl-start-button")
export class ProductStartButton extends LitElement {
  static styles = css`
    :host {
      position: relative;
      height: 100%;
    }
    .wrapper {
      position: relative;
      height: 100%;
    }
    a2k-button {
      height: 100%;
      font: inherit;
      font-weight: var(--font-weight-heavy);
      cursor: pointer;
    }
    .start-button-content {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: var(--taskbar-content-gap);
      white-space: nowrap;
      line-height: 1;
    }
  `;

  @property() active: ModuleName = "overview";
  @state() private menuOpen = false;
  @state() private authenticated = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this.hideOnOutsideClick);
    document.addEventListener("keydown", this.hideOnEscape);
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("sessionchange", this.sessionChanged);
    this.sessionChanged();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.hideOnOutsideClick);
    document.removeEventListener("keydown", this.hideOnEscape);
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("sessionchange", this.sessionChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();
  private sessionChanged = () => {
    this.authenticated = Boolean(session.user);
  };
  private hideOnEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.menuOpen = false;
  };
  private hideOnOutsideClick = (event: MouseEvent) => {
    if (this.menuOpen && !event.composedPath().includes(this))
      this.menuOpen = false;
  };
  private toggle = (event: MouseEvent) => {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  };

  render() {
    return html`<div class="wrapper">
      <cpl-start-menu
        .open=${this.menuOpen}
        .active=${this.active}
        .authenticated=${this.authenticated}
        @close-menu=${() => (this.menuOpen = false)}
      ></cpl-start-menu>
      <a2k-button
        size="small"
        aria-expanded=${String(this.menuOpen)}
        @click=${this.toggle}
      >
        <span class="start-button-content">
          <a2k-icon icon="windows-icon"></a2k-icon>
          <span>${i18n.t("start")}</span>
        </span>
      </a2k-button>
    </div>`;
  }
}

@customElement("cpl-utility-bar")
export class ProductUtilityBar extends LitElement {
  static styles = css`
    .utility-bar {
      display: flex;
      height: 100%;
      align-items: center;
      padding: var(--taskbar-padding) 10px;
      box-shadow: var(--taskbar-utility-bar-shadow);
      white-space: nowrap;
      font-size: 12px;
    }
  `;
  @state() private now = new Date();
  private timer?: number;
  connectedCallback() {
    super.connectedCallback();
    this.timer = window.setInterval(() => (this.now = new Date()), 1000);
  }
  disconnectedCallback() {
    if (this.timer) window.clearInterval(this.timer);
    super.disconnectedCallback();
  }
  render() {
    return html`<div class="utility-bar" aria-label=${i18n.t("operational")}>
      ${this.now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
    </div>`;
  }
}

@customElement("cpl-taskbar")
export class ProductTaskbar extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1000;
      display: block;
      height: var(--taskbar-height);
      width: 100vw;
    }
    .taskbar {
      height: 100%;
      box-sizing: border-box;
      border: var(--taskbar-border);
      border-top: 0;
      border-left: 0;
      box-shadow: var(--taskbar-shadow);
      background: var(--taskbar-color-background);
      padding: var(--taskbar-padding);
      display: flex;
      justify-content: space-between;
      font-family: var(--font-primary);
    }
  `;
  @property() active: ModuleName = "overview";
  render() {
    return html`<div class="taskbar">
      <cpl-start-button
        .active=${this.active}
        @taskbar-logout=${(event: Event) =>
          this.dispatchEvent(
            new CustomEvent("logout", {
              bubbles: true,
              composed: true,
              detail: event,
            }),
          )}
      ></cpl-start-button>
      <cpl-utility-bar></cpl-utility-bar>
    </div>`;
  }
}
